import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { environment } from 'environments/environment';
import { Globals } from 'app/_globals';
import Swal from 'sweetalert2';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ContractorService } from 'app/_services/contractor.service';

@Component({
  selector: 'app-contractor-contacts',
  templateUrl: './contractor-contacts.component.html',
})
export class ContractorContactsComponent implements OnInit {
  selectedContractor: any = [];
  selectedContractorId: any;
  errorAlerts;

  public contractorContacts: any = [];
  selectedContact: any;

  role: any;
  routeRole: any;
  tabitems: any[] = [];

  // pagination
  pageSizes: any = environment.pageSizes;
  pagination: any;

  addContactModal = false;
  addContactForm: FormGroup;
  editContactModal = false;
  editContactForm: FormGroup;

  constructor(
    public globals: Globals,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public fb: FormBuilder,
    public contractorService: ContractorService
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedContractorId = params.id;
    });

    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;

    this.tabitems = [
      { path: '/' + this.routeRole + '/contractors/', title: 'All Contractors', class: 'back-link' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/details', title: 'Details' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/workers', title: 'Workers' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/certificates', title: 'Certificates' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/contacts', title: 'Contacts' },
    ];

    this.resetPagination();

    this.addContactForm = fb.group({
      email: new FormControl('', [Validators.required]),
      name: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required]),
      role: new FormControl('', [Validators.required]),
    });

    this.editContactForm = fb.group({
      email: new FormControl('', [Validators.required]),
      name: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required]),
      role: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit() {
    this.getSelectedContractor(this.selectedContractorId);
  }

  public getSelectedContractor(id) {
    this.contractorService.getContractorById(id).subscribe((data: any) => {
      this.selectedContractor = data;
      this.getAllContractorContacts(this.selectedContractorId, this.pagination);
      if(this.selectedContractor.oem){
        this.tabitems.push({ path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/procedures', title: 'Procedures' })
      }
    });
  }

  public getAllContractorContacts(contractorId, pagination) {
    this.contractorService.getAllContactsByContractorId(contractorId, pagination).subscribe((data: any) => {
      this.contractorContacts = data.body;
      this.pagination.totalRecords = data.totalRecords;
    });
  }

  // Handle Pagination
  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getAllContractorContacts(this.selectedContractorId, this.pagination);
  }

  public resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.getAllContractorContacts(this.selectedContractorId, this.pagination);
  }

  // Toggle Create
  public toggleAddContactModal() {
    this.addContactModal = !this.addContactModal;
  }

  public onAddContractorContactFormSubmit() {
    const values = this.addContactForm.value;
    const body = {
      email: values.email,
      phone: values.phone,
      name: values.name,
      role: values.role,
      maintenanceId: this.selectedContractorId,
    };
    this.contractorService.createContact(body).subscribe((data: any) => {
      this.addContactForm.reset();
      this.getAllContractorContacts(this.selectedContractorId, this.pagination);
      this.toggleAddContactModal();
      Swal.fire('Success!', 'New Contractor Contact Added!', 'success');
    });
  }

  // Handle onRowSelect of a user
  public async onRowSelect(event) {
    this.selectedContact = event.data;
    const { value: action } = await Swal.fire({
      titleText: event.data.name,
      input: 'select',
      inputOptions: {
        edit: 'Edit',
        delete: 'Delete',
      },
      inputPlaceholder: 'Select an option',
      showCancelButton: true,
      inputValidator: (value) => {
        return new Promise((resolve) => {
          resolve(null);
        });
      },
    });

    if (action) {
      if (action === 'edit') {
        this.editContact();
      } else if (action === 'delete') {
        this.deleteContact();
      } else {
        console.log('No valid option selected');
      }
    }
  }

  // Edit Contact
  public editContact() {
    this.toggleEditContactModal();
    this.editContactForm.patchValue({
      email: this.selectedContact.email,
      phone: this.selectedContact.phone,
      role: this.selectedContact.role,
      name: this.selectedContact.name,
    });
  }

  public toggleEditContactModal() {
    this.editContactModal = !this.editContactModal;
  }

  public onEditContactFormSubmit() {
    const values = this.editContactForm.value;
    const body = {
      email: values.email,
      phone: values.phone,
      name: values.name,
      role: values.role,
      id: this.selectedContact.id,
    };
    this.contractorService.editContact(body).subscribe((data: any) => {
      this.editContactForm.reset();
      this.selectedContact = null;
      this.getAllContractorContacts(this.selectedContractorId, this.pagination);
      this.toggleEditContactModal();
      Swal.fire('Success!', 'Contractor Contact Edited!', 'success');
    });
  }

  // Delete Contact
  public deleteContact() {
    Swal.fire({
      title: 'Delete Contact?',
      text: 'You will not be able to revert this!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.contractorService.deleteContact(this.selectedContact.id).subscribe((data: any) => {
          this.selectedContact = null;
          this.getAllContractorContacts(this.selectedContractorId, this.pagination);
          Swal.fire('Deleted!', 'Contact has been removed.', 'success');
        });
      }
    });
  }
}
